import { useEffect } from 'react';
import { subscribeToHydraNavigate } from '@customerio/hydra-nav-state';
import type { useSafeNavigate } from '@/services/router/safe-navigate';
import { replayPendingSnackbars } from '@/services/snackbar';
import type { AppBundle } from '@/utils/app-bundles';

/**
 * Wires the Ember→Hydra navigation handoff and the one-time snackbar replay that
 * rides along with it.
 *
 * An Ember route may enqueue a message via `snackbars.createPersisted` right
 * before handing off to a Hydra route; its own snackbar container unmounts on
 * handoff, so Hydra replays it against its own container. There are two handoff
 * shapes to cover:
 *
 * - **In-app (SPA) handoff** — Ember fires `announceHydraNavigate`; we navigate
 *   and replay in the subscription handler.
 * - **Full document handoff** — e.g. Ember reset-password / confirm-account
 *   hard-redirecting to `/login`. That never fires the subscription, so we also
 *   replay once on mount (mirroring Ember, which ran `replayPending` on boot).
 *
 * The replay clears its sessionStorage key before firing, so running it from
 * both paths is idempotent — whichever runs first wins and the other is a no-op.
 */
export function useHydraNavigationHandoff(safeNavigate: ReturnType<typeof useSafeNavigate>) {
  useEffect(() => {
    replayPendingSnackbars();
  }, []);

  useEffect(() => {
    return subscribeToHydraNavigate((event) => {
      safeNavigate(event.detail.url, {
        shouldFadeOut: event.detail.options?.shouldFadeOut,
        shouldUnrenderOutlet: event.detail.options?.shouldUnrenderOutlet,
        shouldReplaceHistoryEntry: event.detail.options?.shouldReplaceHistoryEntry,
        // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
        targetApp: event.detail.options?.targetApp as AppBundle | undefined,
      });
      replayPendingSnackbars();
    });
  }, [safeNavigate]);
}
